import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { EdgeInstallerCatalog, EdgeInstallerPackage } from '../../core/models';

/**
 * THE STORE PC INSTALLER, FOR THIS ENVIRONMENT. One product per environment - the package carries the address of
 * the middleware it talks to - so the admin of an environment offers that environment's package and no other. The
 * pipeline builds and signs it, publishes it to the environment's bucket, and the middleware lists the bucket and
 * signs a short link on request; nobody here needs an AWS account or a shared drive.
 *
 * The first row is what "Download" hands out: the highest version, the pipeline's clean build over a developer's
 * dirty one, the most recent last. Older packages stay one click away for a store that must be put back.
 */
@Component({
    selector: 'app-edge-installer',
    standalone: true,
    imports: [DatePipe, CardModule, ButtonModule, TableModule, TagModule, MessageModule, TooltipModule],
    template: `
        <p-card>
            <ng-template pTemplate="title">
                <span>Edge installer</span>
                <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help"
                   pTooltip="The package that installs the store Edge on a till PC, built for THIS environment: it carries this middleware's address. The link is good for 15 minutes."></i>
            </ng-template>

            @if (loading()) {
                <p class="text-sm text-surface-500 m-0">Looking for the latest package…</p>
            } @else if (error()) {
                <p-message severity="error" [text]="error()!" />
            } @else if (catalog(); as c) {
                @if (!c.configured) {
                    <p class="text-sm text-surface-500 m-0">
                        This middleware was not told where the installer lives (no <code>EdgeInstaller:Bucket</code>), so there is nothing to offer.
                    </p>
                } @else if (c.problem) {
                    <p-message severity="warn" [text]="c.problem" />
                } @else if (!c.latest) {
                    <p class="text-sm text-surface-500 m-0">
                        No package has been published for this environment yet. The pipeline publishes one the first time it builds a version of <code>installer/edge/VERSION.txt</code>.
                    </p>
                } @else {
                    <div class="flex flex-wrap items-center gap-3">
                        <div class="text-sm flex-1 min-w-64">
                            <div>
                                <b>Version {{ c.latest.version }}</b>
                                <span class="text-surface-500"> · {{ c.latest.environment }} · {{ size(c.latest.sizeBytes) }} · published {{ c.latest.publishedAt | date:'medium' }}</span>
                                @if (c.latest.dirty) {
                                    <p-tag severity="warn" value="developer build" class="ml-2"
                                           pTooltip="Built by hand from a tree with uncommitted changes, not by the pipeline." />
                                }
                            </div>
                            <div class="text-xs text-surface-500 font-mono">{{ c.latest.fileName }}</div>
                        </div>
                        <p-button label="Download Edge installer" icon="pi pi-download"
                                  [loading]="downloading() === c.latest.fileName" (onClick)="download(c.latest)" />
                        @if (c.packages.length > 1) {
                            <p-button [text]="true" size="small"
                                      [label]="showAll() ? 'Hide older packages' : 'Older packages (' + (c.packages.length - 1) + ')'"
                                      (onClick)="showAll.set(!showAll())" />
                        }
                    </div>
                    @if (downloadError()) {
                        <p-message severity="error" [text]="downloadError()!" styleClass="mt-3" />
                    }
                    @if (showAll()) {
                        <p-table [value]="c.packages" styleClass="p-datatable-sm mt-3" [rowHover]="true">
                            <ng-template pTemplate="header">
                                <tr><th>Version</th><th>File</th><th>Published</th><th class="text-right">Size</th><th></th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-p>
                                <tr>
                                    <td>{{ p.version }} @if (p.dirty) { <p-tag severity="warn" value="developer build" /> }</td>
                                    <td class="font-mono text-xs">{{ p.fileName }}</td>
                                    <td>{{ p.publishedAt | date:'medium' }}</td>
                                    <td class="text-right">{{ size(p.sizeBytes) }}</td>
                                    <td class="text-right">
                                        <p-button icon="pi pi-download" [text]="true" size="small" pTooltip="Download this package"
                                                  [loading]="downloading() === p.fileName" (onClick)="download(p)" />
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    }
                }
            }
        </p-card>
    `
})
export class EdgeInstaller implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly catalog = signal<EdgeInstallerCatalog | null>(null);
    readonly showAll = signal(false);
    readonly downloading = signal<string | null>(null);
    readonly downloadError = signal<string | null>(null);

    ngOnInit(): void {
        this.reload();
    }

    reload(): void {
        this.api.getEdgeInstallerCatalog().subscribe({
            next: (c) => { this.catalog.set(c); this.error.set(null); this.loading.set(false); },
            error: (e) => { this.error.set(this.describe(e)); this.loading.set(false); }
        });
    }

    download(p: EdgeInstallerPackage): void {
        this.downloadError.set(null);
        this.downloading.set(p.fileName);
        this.api.getEdgeInstallerDownload(p.fileName).subscribe({
            next: (d) => {
                this.downloading.set(null);
                // The link answers with Content-Disposition: attachment, so this saves the file and the page stays.
                window.location.assign(d.url);
            },
            error: (e) => { this.downloading.set(null); this.downloadError.set(this.describe(e)); }
        });
    }

    size(bytes: number): string {
        return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
    }

    private describe(e: any): string {
        if (e?.status === 403) return 'Your role cannot see the installer (merchants:read).';
        if (e?.status === 404) return e?.error?.error ?? 'That package is no longer in the bucket.';
        return e?.error?.error ?? e?.error?.detail ?? e?.message ?? 'The middleware could not be reached.';
    }
}
